import { errorLog } from '../../utils/logUtils';
import { toKebabCase, normalizeString } from '../../utils/common';
import { NETWORK_STATUS } from '../../utils/constants';
import { getUUID } from '../../utils/monitoring';

export const REQUIRED_KEYS = [
  'componentVariation',
  'componentContent',
  'componentProduct',
];
export const ERROR_REQUIRED_KEYS = ['eventType', 'errorDetail', 'errorOrigin'];
export const TAG_EVENT_TYPES = {
  CLICK: 'click',
  SCROLL: 'scroll',
};

/**
 * Crea el objeto que hay que mandar a Métricas para un evento de Página
 * @param {Object} data con todos los parámetros necesarios.
 */

export type clientIDsType = {
  ga: string;
  sessId: string;
  pageId: string;
};

export type PageEventType = {
  event?: string;
  eventType?: string;
  tabId?: string;
  pageName?: string;
  pageProductCode?: string;
  pageProductName?: string;
  clientIDs?: clientIDsType;
  contextGaCookie?: any;
  contextSessionId?: any;
  contextPageId?: any;
  userCUCFijoHash?: string;
  userCUCMovilHash?: string;
  userCUCPrepagoHash?: string;
  userGl?: string;
  userIPHash?: string;
  userOperadorHash?: string;
  userTelefonoFijoHash?: string;
  userTelefonoMovilHash?: string;
  userIdPartyHash?: string;
  userType?: string;
  userConsentCookieOneTrust?: string;
  pageSegment?: string;
  contextURL?: string;
  testVersion?: string;
  testName?: string;
  userClient?: boolean;
  userPriority?: string;
  contextSeoMeta?: string;
};

export type PageEventTypeForMetrics = {
  event: string;
  'event-type': string;
  'context-tab-id': string;
  'page-name': string;
  'page-product-code': string;
  'page-product-name': string;
  'context-ga-cookie': string;
  'context-session-id': string;
  'context-page-id': string;
  'user-cuc-fijo-hash': string;
  'user-cuc-movil-hash': string;
  'user-cuc-prepago-hash': string;
  'user-ip-hash': string;
  'user-operador-hash': string;
  'user-gl': string;
  'user-telefono-fijo-hash': string;
  'user-telefono-movil-hash': string;
  'user-id-party-hash': string;
  'user-type': string;
  'user-consent-cookie-onetrust': string;
  'page-segment': string;
  'context-url': string;
  'test-version'?: string;
  'test-name'?: string;
  'user-client': boolean;
  'user-priority': string;
  'context-seo-meta': string;
};

export const createPageEventObject = (
  data: PageEventType,
): PageEventTypeForMetrics => {
  if (!data) {
    return null;
  }

  const { clientIDs, userType: userTypeFromData } = data;

  const {
    event = 'cms-page',
    eventType = '',
    tabId = '',
    pageName = '',
    pageProductCode = '',
    pageProductName = '',
    contextGaCookie = (clientIDs && clientIDs.ga) || null,
    contextSessionId = (clientIDs && clientIDs.sessId) || null,
    contextPageId = (clientIDs && clientIDs.pageId) || null,
    userCUCFijoHash = '',
    userCUCMovilHash = '',
    userCUCPrepagoHash = '',
    userIPHash = '',
    userOperadorHash = '',
    userTelefonoFijoHash = '',
    userTelefonoMovilHash = '',
    userIdPartyHash = '',
    userType = '',
    userConsentCookieOneTrust = '',
    pageSegment = '',
    contextURL = '',
    testVersion = '',
    testName = '',
    userClient = !!userTypeFromData,
    userPriority = '',
    contextSeoMeta = '',
    userGl = '',
  } = data;

  return {
    event,
    'event-type': eventType,
    'context-tab-id': tabId,
    'page-name': pageName,
    'page-product-code': pageProductCode,
    'page-product-name': pageProductName,
    // client id de ga
    'context-ga-cookie': contextGaCookie,
    // id de sesión de la navegación del usuario
    'context-session-id': contextSessionId,
    // page id de la página
    'context-page-id': contextPageId,
    'user-cuc-fijo-hash': userCUCFijoHash,
    'user-cuc-movil-hash': userCUCMovilHash,
    'user-cuc-prepago-hash': userCUCPrepagoHash,
    'user-gl': userGl,
    'user-ip-hash': userIPHash,
    'user-operador-hash': userOperadorHash,
    'user-telefono-fijo-hash': userTelefonoFijoHash,
    'user-telefono-movil-hash': userTelefonoMovilHash,
    'user-id-party-hash': userIdPartyHash,
    // telco o legado
    'user-type': userType,
    // estado consentimientos cookies del usuario
    'user-consent-cookie-onetrust': userConsentCookieOneTrust,
    // segmento de navegación de la página
    'page-segment': pageSegment,
    // url de la página
    'context-url': contextURL,
    'test-version': testVersion,
    'test-name': testName,
    // true o false si es cliente o no
    'user-client': userClient,
    // true o false si es priority o no
    'user-priority': userPriority,
    // meta description de seo
    'context-seo-meta': contextSeoMeta,
  };
};
/* with event and eventType we use same structure for tagging object,
and event for metrics, and many more for customizer */
export type TaggingObjectType = {
  event?: string;
  eventType?: string;
  componentName: string;
  componentVariation: string;
  componentContent: string;
  componentProduct: string;
  componentPerso: any;
  debug?: boolean;
  customizerId?: string;
};

export type TaggingObject4MetricsType = {
  event?: string;
  'event-type'?: string;
  'component-name': string;
  'component-variation': string;
  'component-content': string;
  'component-product': string;
  'component-perso': any;
  debug?: boolean;
  'customizer-id'?: string;
};

export type TaggingErrorObjectType = {
  eventType: string;
  errorDetail: string;
  errorOrigin: string;
};

export type TaggingErrorObject4MetricsType = {
  event: string;
  'event-type': string;
  'error-detail': string;
  'error-origin': string;
};

/**
 * Crea el objeto con la estructura adecuada para un evento de componente de métricas
 * @param {String} eventType
 * @param {String} componentName
 * @param {String} componentVariation
 * @param {String} componentContent
 * @param {String} componentProduct
 * @param {Object} componentPerso
 * @param {String} event
 */
export const createEventObject = (
  inputObj: any,
  eventType = TAG_EVENT_TYPES.CLICK,
): TaggingObject4MetricsType => {
  const finalRes: TaggingObject4MetricsType = {
    event: 'cms-component',
    'event-type': eventType,
    'component-name': '',
    'component-variation': '',
    'component-content': '',
    'component-product': '',
    'component-perso': {},
  };

  const keys = inputObj ? Object.keys(inputObj) : [];
  keys.forEach((key) => {
    const resKey = toKebabCase(key);
    let resValue = inputObj[key];
    if (typeof resValue === 'string') {
      resValue = normalizeString(resValue);
    }
    finalRes[resKey] = resValue;
    finalRes[resKey] = finalRes[resKey] || '';
  });

  return finalRes;
};

export const checkEventObj = (taggingInfo: TaggingObjectType): boolean => {
  let res = true;
  REQUIRED_KEYS.forEach((key) => {
    if (typeof taggingInfo[key] === 'undefined') {
      res = false;
    }
  });
  if (taggingInfo.debug) {
    res = true;
  }
  return res;
};

/**
 * Envía a la cola adecuada de métricas un evento de componente.
 * @param {*} eventType
 * @param {*} componentName
 * @param {*} componentVariation
 * @param {*} componentContent
 * @param {*} componentProduct
 * @param {*} componentPerso
 */

export const sendCMSEvent = (inputObj: TaggingObjectType): void => {
  const checkObj = checkEventObj(inputObj);
  const object = createEventObject(inputObj);
  try {
    if (checkObj) {
      window.nookInfoExchange.sendMessage('Metrics', object);
    } else {
      errorLog(
        'TaggingInfo is no properly defined',
        '[checkEventObj] no right event structure',
      );
    }
  } catch (err) {
    // TODO monirorizar donde toque.
  }
};

export const buildCMSEventObj = (
  kind = 'print',
  status: string,
  eventFamily = 'cms-component',
  id: string,
  customizerId: string,
  taggingInfo: TaggingObjectType,
): TaggingObjectType => {
  const componentUuid = getUUID();
  const newEvent = `${kind}-${status}`;
  const event = {
    ...taggingInfo,
    event: eventFamily,
    eventType: newEvent,
    componentUuid,
  };
  if (id) {
    event.componentName = id;
  }
  event.customizerId = customizerId;
  if (status === NETWORK_STATUS.FETCHING) {
    event.componentUuid = componentUuid;
  }
  // set necesary props to avoid checkEventObj
  if (!event.componentName) {
    event.componentName = '';
  }

  if (!event.componentVariation) {
    event.componentVariation = '';
  }

  if (!event.componentContent) {
    event.componentContent = '';
  }

  if (!event.componentProduct) {
    event.componentProduct = '';
  }
  return event;
};

/**
 * Funcion que recibe una cadena y la normaliza, reemplazando las / por :
 * @param {string} unnormalizedString cadena sin normalizar
 * @return {string} normalizedString cadena normalizada
 */
const replaceSlashToColon = (unnormalizedString: string): string => {
  const normalizedString = unnormalizedString.replace(/\//g, ':');
  return normalizedString;
};

/**
 * Funcion que normaliza y retorna el pathname actual
 * @return {string} normalizedUrl pathname normalizado
 */
export const convertPathToPageName = (): string => {
  let normalizedUrl = '';
  if (window && window.location) {
    const { pathname } = window.location;
    const pathNameWithoutFirstSlash = pathname.replace('/', '');
    normalizedUrl = replaceSlashToColon(
      pathNameWithoutFirstSlash,
    ).toLowerCase();
  }
  return normalizedUrl;
};
